import axios from 'axios'
import store from '@/store'
import NProgress from 'nprogress'

//  Base HTTP
const $baseHTTP = axios.create({
  baseURL: `${process.env.CAMUNDA_PATH}/rest`,
  headers: {
    Accept: 'application/hal+json, application/json; q=0.5',
  },
})

const i18nErrors = {
  TaskAlreadyClaimedException: 'Задача уже обрабатывается кем-то другим',
}

function handleError(error) {
  const errorMessage = error
                      && error.response
                      && error.response
                      && error.response.data
                      && error.response.data.type
                      && i18nErrors[error.response.data.type]
    ? i18nErrors[error.response.data.type]
    : 'Произошла ошибка'
  console.log(error.response ? error.response : error)
  store.commit('task/set', { type: 'submitMode', value: false })
  store.dispatch('tools/setSnackbar', errorMessage)
  throw error
}

$baseHTTP.interceptors.request.use((cfg) => {
  NProgress.start()
  store.dispatch('tools/setLoading', true)
  return cfg
}, (error) => {
  NProgress.done()
  store.dispatch('tools/setLoading', false)
  return Promise.reject(error)
})

$baseHTTP.interceptors.response.use((cfg) => {
  NProgress.done()
  store.dispatch('tools/setLoading', false)
  return cfg
// eslint-disable-next-line consistent-return
}, (error) => {
  NProgress.done()
  store.dispatch('tools/setLoading', false)
  return Promise.reject(error)
})

const api = {
  user: {
    profile(username) {
      return $baseHTTP.get(`/user/${username}/profile`).catch((error) => { handleError(error) })
    },

    group(payload) {
      return $baseHTTP.get('/group', { params: payload }).catch((error) => { handleError(error) })
    },

    initialize(payload) {
      return $baseHTTP({
        method: 'get',
        url: `authorization/initialize-user/${payload.userName}/${payload.firstName}/${payload.lastName}/${payload.email}`,
        baseURL: `${process.env.CAMUNDA_PATH}/rest-wf`,
        data: payload }).catch((error) => { handleError(error) })
    },

    query(payload) {
      return $baseHTTP.get('/user', { params: payload }).catch((error) => { handleError(error) })
    },

    permission(permissionName, permissionValue, resourceName, resourceType, resourceId) {
      return $baseHTTP.get(`/authorization/check?permissionName=${permissionName}&permissionValue=${permissionValue}`
        + `&resourceName=${resourceName}&resourceType=${resourceType}&resourceId=${resourceId}`).catch((error) => { handleError(error) })
    },

    authorization(resourceType, permission) {
      return $baseHTTP.get(`/authorization/check/${resourceType}/${permission}`, { baseURL: `${process.env.CAMUNDA_PATH}/rest-wf` }).catch((error) => { handleError(error) })
    },

    create(payload) {
      return $baseHTTP.post('/user/create', payload).catch((error) => { handleError(error) })
    },
    getMemberOf(groupId) {
      return $baseHTTP.get(`/user?memberOfGroup=${groupId}`).catch((error) => { handleError(error) })
    },
    list(userId) {
      return $baseHTTP.get(`/user?id=${userId}`).catch((error) => { handleError(error) })
    },
  },

  group: {
    list(nameLike) {
      return $baseHTTP.get(`/group?nameLike=${nameLike}%`).catch((error) => { handleError(error) })
    },
    create(payload) {
      return $baseHTTP.post('/group/create', payload).catch((error) => { handleError(error) })
    },
    createMember(groupId, userId) {
      return $baseHTTP.put(`/group/${groupId}/members/${userId}`).catch((error) => { handleError(error) })
    },
    deleteMember(groupId, userId) {
      return $baseHTTP.delete(`/group/${groupId}/members/${userId}`).catch((error) => { handleError(error) })
    }
  },

  task: {
    filters() {
      return $baseHTTP.get('/filter?itemCount=true&resoureType=Task').catch((error) => { handleError(error) })
    },

    history(processInstanceId) {
      return $baseHTTP.get(`/history/task?processInstanceId=${processInstanceId}&finished=true&sortBy=endTime&sortOrder=asc`).catch((error) => { handleError(error) })
    },

    list(filterId, page, sort) {
      return $baseHTTP.post(`/filter/${filterId}/list?firstResult=`
                          + `${(page.current - 1) * page.per}&maxResults=${page.per}`, sort).catch((error) => { handleError(error) })
    },

    news(page, sort) {
      return $baseHTTP.post('/filter/949831fa-0661-11ed-b79c-0242ac17000b/list?firstResult='
                          + `${(page.current - 1) * page.per}&maxResults=${page.per}`, sort).catch((error) => { handleError(error) })
    },

    get(taskId) {
      return $baseHTTP.get(`/task/${taskId}`).catch((error) => { handleError(error) })
    },

    variables: {
      get(taskId) {
        const cfg = { params: { deserializeValues: false } }
        return $baseHTTP.get(`/task/${taskId}/variables`, cfg).catch((error) => { handleError(error) })
      },
      update(taskId, payload) {
        return $baseHTTP.post(`/task/${taskId}/variables`, payload).catch((error) => { handleError(error) })
      },
    },

    claim(taskId, userName) {
      return $baseHTTP.post(`/task/${taskId}/claim`, { userId: userName }).catch((error) => { handleError(error) })
    },

    unclaim(taskId) {
      return $baseHTTP.post(`/task/${taskId}/unclaim`).catch((error) => { handleError(error) })
    },

    submit(taskId, payload) {
      return $baseHTTP.post(`/task/${taskId}/submit-form`, payload).catch((error) => { handleError(error) })
    },

    search(payload) {
      return $baseHTTP.post('/history/task', payload).catch((error) => { handleError(error) })
    },
  },

  definition: {
    list() {
      const cfg = { baseURL: `${process.env.CAMUNDA_PATH}/rest-wf` }
      return $baseHTTP.get(`/process-definition?user=${store.getters['auth/profile'].userName}&hash=${store.getters['auth/groupsHash']}`, cfg).catch((error) => { handleError(error) })
    },

    get(definitionId) {
      return $baseHTTP.get(`/process-definition/${definitionId}`).catch((error) => { handleError(error) })
    },

    deploy(processName, deploymentSource, file) {
      const formData = new FormData()
      formData.append('deployment-name', processName)
      formData.append('deployment-source', deploymentSource)
      formData.append('data', file)

      return $baseHTTP.post('/deployment/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
  },

  process: {
    start(key, payload) {
      return $baseHTTP.post(`/process-definition/key/${key}/start`, payload).catch((error) => { handleError(error) })
    },

    diagramXml(processDefinitionId) {
      return $baseHTTP.get(`/process-definition/${processDefinitionId}/xml`).catch((error) => { handleError(error) })
    },

    variables(processInstanceId) {
      const cfg = { params: { deserializeValues: false, processInstanceId: processInstanceId } }
      return $baseHTTP.get('/history/variable-instance', cfg).catch((error) => { handleError(error) })
    },

    searchVariables(processInstanceIds, variable) {
      const cfg = { processInstanceIdIn: processInstanceIds, variableName: variable }
      return $baseHTTP.post('/history/variable-instance?deserializeValues=false', cfg).catch((error) => { handleError(error) })
    },

    get(processInstanceId) {
      return $baseHTTP.get(`/history/process-instance/${processInstanceId}`).catch((error) => { handleError(error) })
    },

    tasks(processInstanceId) {
      const cfg = { params: { deserializeValues: false, processInstanceId: processInstanceId, sortBy: 'startTime', sortOrder: 'asc' }, baseURL: `${process.env.CAMUNDA_PATH}/rest-wf` }
      return $baseHTTP.get('/history/task', cfg).catch((error) => { handleError(error) })
    },

    count(payload) {
      return $baseHTTP.post('/history/process-instance/count', payload).catch((error) => { handleError(error) })
    },

    search(page, payload) {
      return $baseHTTP.post('/history/process-instance?firstResult='
      + `${(page.current - 1) * page.per}&maxResults=${page.per}`, payload).catch((error) => { handleError(error) })
    },

    cancel(processInstanceId){
      const payload = {skipCustomListeners: true, skipIoMappings: true, skipSubprocesses: true}
      return $baseHTTP.delete(`/process-instance/${processInstanceId}`, payload).catch((error) => { handleError(error) })
    }
  },
}

export default api



// WEBPACK FOOTER //
// ./src/services/camunda.js